import { joinStringsWithSeparator } from '@modules/common/utils';
import { IntegrationSource } from '@modules/integrationsV2/types';
import { getLinkableIntegrationTypes } from '@modules/integrationsV2/utils';
import { AddLinkRounded } from '@mui/icons-material';
import { Alert, Button, Link, Stack, Typography } from '@mui/material';
import { IntegrationType } from 'generated/graphql';
import { capitalize } from 'lodash';

const LINK_INTEGRATION_METADATA_HELP_CENTER_URL =
  'https://helpcenter.enterpret.com/en/articles/8611269-syncing-users-and-accounts#h_e5b7e3eacf';

interface LinkIntegrationMetadataHeaderProps {
  integrationSource: IntegrationSource;
}

export const LinkIntegrationMetadataHeader = ({
  integrationSource,
}: LinkIntegrationMetadataHeaderProps) => {
  const linkableIntegrationTypes = getLinkableIntegrationTypes(
    integrationSource.integrationType,
  );

  const supportedTypesLabel = joinStringsWithSeparator({
    strings: linkableIntegrationTypes.map(capitalize),
    lastSeparatorWord: 'and',
  });

  const getDataLabelForIntegrationType = (type: IntegrationType) => {
    switch (type) {
      case IntegrationType.Feedback:
        return 'feedback';
      case IntegrationType.User:
        return 'user data';
      case IntegrationType.Account:
        return 'account data';
      case IntegrationType.Cohorts:
        return 'user cohorts';
      default:
        return '';
    }
  };

  const supportedTypesDataLabel = joinStringsWithSeparator({
    strings: linkableIntegrationTypes.map(getDataLabelForIntegrationType),
    lastSeparatorWord: 'and',
  });

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <AddLinkRounded
          sx={{
            color: 'text.secondary',
          }}
        />
        <Typography>Link with {supportedTypesLabel} integrations</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" pl={4}>
        Connect{' '}
        {getDataLabelForIntegrationType(integrationSource.integrationType)} from{' '}
        {integrationSource.channel.displayName} with {supportedTypesDataLabel}{' '}
        to enhance feedback analytics.{' '}
        <Link
          href={LINK_INTEGRATION_METADATA_HELP_CENTER_URL}
          target="_blank"
          underline="hover"
        >
          Learn more.
        </Link>
      </Typography>
      {integrationSource.integrationType === IntegrationType.Cohorts && (
        <Alert
          severity="warning"
          sx={{
            border: '1px solid',
            borderColor: 'warning.main',
            mt: 2,
          }}
          action={
            <Button
              color="warning"
              onClick={() => {
                // TODO: Add link to helpcenter docs
                window.open('#');
              }}
              sx={{
                whiteSpace: 'nowrap',
                minWidth: 'max-content',
              }}
            >
              Know Why
            </Button>
          }
        >
          Your cohorts are currently directly linked with Feedback Integrations.
          This will be deprecated in the future. We recommend linking cohorts
          with a User Integration.
        </Alert>
      )}
    </Stack>
  );
};
